import { Injectable } from '@angular/core';
import { appstraxAuth } from '@appstrax/services/auth';
import { environment } from '../../environments/environment';

export type MachineApiRequestInit = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
};

@Injectable({ providedIn: 'root' })
export class MachineApiService {
  private baseUrl(): string {
    return String(environment.machineApiUrl ?? '').replace(/\/+$/, '');
  }

  private async authHeader(): Promise<Record<string, string>> {
    const token = await appstraxAuth.getAuthToken();
    if (!token) throw new Error('Not authenticated');
    return { Authorization: `Bearer ${token}` };
  }

  async request<T = unknown>(path: string, init: MachineApiRequestInit = {}): Promise<T> {
    const url = `${this.baseUrl()}${path.startsWith('/') ? '' : '/'}${path}`;
    const auth = await this.authHeader();
    const headers: Record<string, string> = {
      ...auth,
      ...(init.headers ?? {}),
    };

    const res = await fetch(url, { ...init, headers });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Machine API request failed (${res.status})`);
    }

    // Handle empty responses gracefully.
    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      return (await res.text()) as unknown as T;
    }
    return (await res.json()) as T;
  }

  async postJson<T = unknown>(path: string, body: any, init: MachineApiRequestInit = {}): Promise<T> {
    return this.request<T>(path, {
      ...init,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers ?? {}),
      },
      body: JSON.stringify(body ?? {}),
    });
  }

  /**
   * POST JSON but return the raw Response for streaming (SSE).
   * The caller is responsible for reading/closing the body.
   */
  async streamPostJson(path: string, body: any, init: MachineApiRequestInit = {}): Promise<Response> {
    const url = `${this.baseUrl()}${path.startsWith('/') ? '' : '/'}${path}`;
    const auth = await this.authHeader();
    const headers: Record<string, string> = {
      ...auth,
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    };

    return fetch(url, {
      ...init,
      method: 'POST',
      headers,
      body: JSON.stringify(body ?? {}),
    });
  }
}

